// @flow
import { colors } from 'bench-style-constants';
import { TextBlock } from 'bench-internal-theme';
import {
  BulkEditFormContainer,
  DataEntryHeaderRow,
  SelectedDataEntriesConsumer,
  // $FlowFixMe - TS file
} from 'general-ledger';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import doesDataEntryContainSearchString from './doesDataEntryContainSearchString';
import FollowUpViewDataEntryRow from './FollowUpViewDataEntryRow';
import type { DataEntry } from './types';
import FollowUpViewClearNotificationsButtonContainer from './FollowUpViewClearNotificationsButtonContainer';

const bulkEditFormHeight = 164;

const FollowUpViewLayout = styled.div`
  bottom: ${({ isBulkEditFormOpen }) => (isBulkEditFormOpen ? `${bulkEditFormHeight}px` : '0')};
  left: 0;
  overflow: auto;
  padding: 32px;
  position: absolute;
  right: 0;
  top: 0;
`;

const BulkEditFormArea = styled.div`
  background: ${colors.pearl};
  bottom: 0;
  box-shadow: 0 -1px ${colors.silt};
  height: ${bulkEditFormHeight}px;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
`;

const FollowUpTableLayout = styled.div`
  margin: 0 -32px;
  padding: 0 0 60px;
  max-width: 1700px;
`;

const FollowUpViewDescription = styled(TextBlock)`
  max-width: 800px;
  padding-top: 12px;
`;

const ClearNotificationsButtonLayout = styled.div`
  padding: 4px 32px 12px 32px;
  text-align: right;
`;

const followUpColumnHeaders = [
  { heading: 'Date', width: '72px;' },
  { heading: 'Transaction Name', width: 'auto' },
  { heading: 'Debit', width: '72px', align: 'right' },
  { heading: 'Credit', width: '72px', align: 'right' },
  { heading: 'Client selected this ledger account', width: '24px', icon: 'circleCheck' },
  { heading: 'Ledger', width: '220px' },
  { heading: 'Notes', width: 'auto' },
  { heading: 'Has attachment', width: '24px', icon: 'paperclip' },
  { heading: 'Has Add-On', width: '24px', icon: 'link' },
];

type FollowUpViewProps = {
  dataEntriesWithOutstandingComments: Array<DataEntry>,
  search: string,
  selectedDataEntry: ?DataEntry,
  toggleDataEntry: DataEntry => any,
};

const FollowUpView = ({
  dataEntriesWithOutstandingComments,
  search,
  selectedDataEntry,
  toggleDataEntry,
}: FollowUpViewProps) => (
  <SelectedDataEntriesConsumer>
    {({ selectedDataEntryIds }: any) => {
      const isSearchStringSet = search.trim() !== '';
      const areAnyDataEntriesSelectedForBulkEdit = !!selectedDataEntryIds.length;
      const displayedDataEntries = dataEntriesWithOutstandingComments.filter(dataEntry =>
        isSearchStringSet ? doesDataEntryContainSearchString(dataEntry, search) : true,
      );
      return (
        <Fragment>
          <FollowUpViewLayout
            data-test-id="follow-up-view"
            isBulkEditFormOpen={areAnyDataEntriesSelectedForBulkEdit}
          >
            <TextBlock styleType="title">Follow Up</TextBlock>
            <FollowUpViewDescription color={colors.smoke} styleType="small">
              Please review and address all client feedback as a priority to provide them with the
              best possible Bench experience. If no action is required, leave an internal note
              explaining why and consider sending them a message to let them know.
            </FollowUpViewDescription>
            <FollowUpTableLayout>
              <ClearNotificationsButtonLayout>
                <FollowUpViewClearNotificationsButtonContainer
                  availableDataEntriesWithOutstandingComments={displayedDataEntries}
                />
              </ClearNotificationsButtonLayout>
              <DataEntryHeaderRow
                dataEntriesUnderHeader={displayedDataEntries}
                columns={followUpColumnHeaders}
              />
              {displayedDataEntries.map((dataEntry, index) => (
                <FollowUpViewDataEntryRow
                  key={dataEntry.id}
                  dataEntry={dataEntry}
                  hasBackgroundColor={index % 2}
                  isSelected={!!selectedDataEntry && selectedDataEntry.id === dataEntry.id}
                  search={search}
                  toggleDataEntry={toggleDataEntry}
                />
              ))}
            </FollowUpTableLayout>
          </FollowUpViewLayout>
          {areAnyDataEntriesSelectedForBulkEdit && (
            <BulkEditFormArea>
              <BulkEditFormContainer
                availableDataEntries={displayedDataEntries}
                data-test-id="bulk-edit-form"
                shouldClearSelectionAfterSubmit={false}
              />
            </BulkEditFormArea>
          )}
        </Fragment>
      );
    }}
  </SelectedDataEntriesConsumer>
);

export default FollowUpView;
